import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, UserPlus, X } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu.tsx';
import { config } from '@/config';
import { useAuth } from '@/hooks/useAuth.ts';
import { HELD_ACCOUNTS_KEY, HeldAccount, useHeldAccounts } from '@/hooks/useHeldAccounts.ts';
import { MAX_ACCOUNTS, switchAccount } from '@/lib/activeAccount.ts';
import { forgetPushAccount } from '@/lib/pushAccounts.ts';

function displayName(account: HeldAccount): string {
  return `${account.firstName} ${account.lastName}`.trim() || account.email;
}

/** Where the add-account sign-in comes back to: this page. */
function addAccountUrl(email?: string): string {
  const params = new URLSearchParams({
    add: '1',
    returnTo: `${window.location.pathname}${window.location.search}`,
  });
  if (email) params.set('email', email);
  return `/signin?${params.toString()}`;
}

/**
 * The other accounts this browser is signed in to, inside the profile menu
 * (docs/MultiAccountSessions.md §9): switch to one, sign one out, or add another.
 *
 * Each is signed out from here without leaving this tab's account. One signed out elsewhere
 * stays listed as "Signed out", with sign-in and remove, until it is removed.
 */
export function AccountMenuSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: held = [] } = useHeldAccounts(!!user);
  if (!user) return null;

  const others = held.filter((account) => account.id !== user.id);
  const full = held.length >= MAX_ACCOUNTS;

  // A bare instance: the app's interceptors would read a 401 here as this tab's session ending.
  const signOut = async (account: HeldAccount) => {
    await axios
      .create()
      .post(`${config.backendApi}/api/auth/logout?account=${encodeURIComponent(account.id)}`, '', {
        withCredentials: true,
      })
      .catch(() => undefined);
    await forgetPushAccount(account.id);
    await queryClient.invalidateQueries({ queryKey: HELD_ACCOUNTS_KEY });
  };

  return (
    <>
      {others.map((account) => (
        <div key={account.id} className="flex items-stretch">
          <DropdownMenuItem
            className="flex-1 min-w-0 cursor-pointer"
            onSelect={() => {
              if (account.signedIn) {
                // The other account may not reach this page's organization, so it starts at home.
                switchAccount(account.id, '/home');
              } else {
                window.location.assign(addAccountUrl(account.email));
              }
            }}
          >
            <div className="min-w-0">
              <div className="text-sm truncate">{displayName(account)}</div>
              <div className="text-xs text-muted-foreground truncate">
                {account.signedIn ? account.email : `Signed out · sign in as ${account.email}`}
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer px-2"
            aria-label={account.signedIn ? `Sign out ${account.email}` : `Remove ${account.email}`}
            title={account.signedIn ? 'Sign out' : 'Remove'}
            onSelect={(event) => {
              // Keep the menu open: the list updates in place.
              event.preventDefault();
              void signOut(account);
            }}
          >
            {account.signedIn ? <LogOut className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </DropdownMenuItem>
        </div>
      ))}
      <DropdownMenuItem
        className="gap-2 cursor-pointer"
        disabled={full}
        onSelect={() => { window.location.assign(addAccountUrl()); }}
      >
        <UserPlus className="w-4 h-4" />
        Add another account
      </DropdownMenuItem>
      {full && (
        <p className="px-2 pb-1.5 text-xs text-muted-foreground">
          You&rsquo;re signed in to {MAX_ACCOUNTS} accounts. Sign out of one to add another.
        </p>
      )}
      <DropdownMenuSeparator />
    </>
  );
}
